
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Product } from '@/types/portfolio';

interface CertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  certification: {
    id: number;
    standard: string;
    certifiedOn: string;
    nextVerification: string;
    certificateNumber: string;
    status: string;
  };
}

export const CertificateModal = ({ isOpen, onClose, product, certification }: CertificateModalProps) => {
  const handleDownloadPDF = () => {
    // For now, this is a placeholder - in a real app, this would generate and download a PDF
    console.log('Downloading PDF certificate for:', certification.certificateNumber);
    // You could implement PDF generation using libraries like jsPDF or react-pdf
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Green Seal Certificate</DialogTitle>
        </DialogHeader>
        
        <div className="relative bg-white p-8 border-8 border-green-700 rounded-lg">
          {/* Green background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-green-100 rounded-lg"></div>
          
          {/* Certificate content */}
          <div className="relative z-10 text-center space-y-6">
            {/* Green Seal Logo */}
            <div className="flex justify-center mb-6">
              <div className="bg-green-700 text-white px-6 py-3 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 bg-green-700 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-lg">GREEN SEAL</div>
                    <div className="font-bold text-lg">CERTIFIED</div>
                    <div className="text-sm">greenseal.org</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Certificate title */}
            <h1 className="text-2xl font-bold text-gray-800">
              Green Seal® Proudly Presents Certification to
            </h1>

            {/* Product/Company name */}
            <h2 className="text-4xl font-bold text-gray-900 my-8">
              {product.name}
            </h2>
            
            {/* Manufacturer location */}
            <p className="text-lg text-gray-700">
              {product.manufacturer || "Manufacturer Name"}, United States
            </p>

            {/* Certification statement */}
            <div className="my-8">
              <p className="text-lg text-gray-700 mb-4">Green Seal® certifies that</p>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">{product.name}</h3>
              <p className="text-lg text-gray-700 mb-4">
                is licensed to use the Green Seal Certification Mark and complies with the following Green Seal Standard(s):
              </p>
              <p className="text-xl font-semibold text-gray-800">
                {certification.standard} Commercial and Institutional Cleaning Services
              </p>
            </div>

            {/* Signature area */}
            <div className="flex justify-end mt-12 mb-8">
              <div className="text-right">
                <div className="border-b-2 border-gray-400 w-48 mb-2"></div>
                <div className="text-sm text-gray-600">
                  <p className="font-semibold">Doug Gatlin, CEO</p>
                  <p>Green Seal, Inc.</p>
                  <p>601 13th St NW, 12th Floor</p>
                  <p>Washington, DC 20005</p>
                </div>
              </div>
            </div>

            {/* Certification date */}
            <div className="text-left">
              <p className="text-lg font-semibold text-gray-800">
                Certification Date: {new Date(certification.certifiedOn).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            {/* Certificate number */}
            <div className="text-center pt-4 border-t border-gray-300">
              <p className="text-sm text-gray-600">Certificate Number: {certification.certificateNumber}</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-center mt-6">
          <Button onClick={handleDownloadPDF} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
